import React, { createContext, useContext, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import PrintDelivery, { PrintDeliveryFile } from '@/models/printDelivery.ts'

export interface PrintDeliveryContextActions {
  refetch: () => Promise<any>;
  confirmDelivery: () => Promise<void>;
  uploadFile: (file: File) => Promise<PrintDeliveryFile>;
  deleteFile: (fileId: string) => Promise<void>;
  updateDelivery: (data: Partial<PrintDelivery>) => Promise<void>;
}

// Define the context data interface
interface PrintDeliveryContextData {
  printDelivery: PrintDelivery | null;
  isLoading: boolean;
  isConfirming: boolean;
  isUploading: boolean;
  isUpdating: boolean;
  error: Error | null;
}

// Create the context with default values
export const PrintDeliveryContext = createContext<PrintDeliveryContextData & PrintDeliveryContextActions>({
  printDelivery: null,
  isLoading: false,
  isConfirming: false,
  isUploading: false,
  isUpdating: false,
  error: null,
  async refetch() {},
  async confirmDelivery() {},
  async uploadFile() { return {} as PrintDeliveryFile },
  async deleteFile() {},
  async updateDelivery() {},
})

// Create the provider component
export function PrintDeliveryProvider({
                                        children,
                                        deliveryId
                                      }: {
  children: React.ReactNode,
  deliveryId: string
}) {
  const {
    getPrintDelivery,
    confirmPrintDelivery,
    uploadPrintDeliveryFile,
    deletePrintDeliveryFile,
    updatePrintDelivery
  } = useClient()

  const [error, setError] = useState<Error | null>(null)

  // Fetch print delivery data
  const {
    data: printDelivery,
    isLoading,
    refetch,
    isRefetching
  } = useQuery({
    queryKey: ['printDelivery', deliveryId],
    queryFn: async () => {
      try {
        const res = await getPrintDelivery(deliveryId)
        setError(null)
        return res.data
      } catch (e) {
        if (e instanceof Error) {
          setError(e)
        }
        return null
      }
    },
    enabled: !!deliveryId,
  })

  // Confirm delivery mutation
  const confirmMutation = useMutation({
    mutationFn: async () => {
      return await confirmPrintDelivery(deliveryId)
    },
    onSuccess: () => {
      refetch()
    },
    onError: (e: Error) => {
      setError(e)
    }
  })

  // Upload file mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const response = await uploadPrintDeliveryFile(deliveryId, file)
      return response.data
    },
    onSuccess: () => {
      refetch()
    },
    onError: (e: Error) => {
      setError(e)
    }
  })

  // Delete file mutation
  const deleteMutation = useMutation({
    mutationFn: async (fileId: string) => {
      await deletePrintDeliveryFile(fileId)
    },
    onSuccess: () => {
      refetch()
    },
    onError: (e: Error) => {
      setError(e)
    }
  })

  // Update delivery mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<PrintDelivery>) => {
      await updatePrintDelivery(deliveryId, data)
    },
    onSuccess: () => {
      refetch()
    },
    onError: (e: Error) => {
      setError(e)
    }
  })

  // Context actions
  async function confirmDelivery() {
    await confirmMutation.mutateAsync()
  }

  async function uploadFile(file: File) {
    return await uploadMutation.mutateAsync(file)
  }

  async function deleteFile(fileId: string) {
    await deleteMutation.mutateAsync(fileId)
  }

  async function updateDelivery(data: Partial<PrintDelivery>) {
    await updateMutation.mutateAsync(data)
  }

  return (
    <PrintDeliveryContext.Provider value={{
      printDelivery: printDelivery || null,
      isLoading: isLoading || isRefetching,
      isConfirming: confirmMutation.isPending,
      isUploading: uploadMutation.isPending,
      isUpdating: updateMutation.isPending,
      error,
      refetch,
      confirmDelivery,
      uploadFile,
      deleteFile,
      updateDelivery,
    }}>
      {children}
    </PrintDeliveryContext.Provider>
  )
}

// Custom hook to use the PrintDelivery context
export function usePrintDelivery() {
  return useContext(PrintDeliveryContext)
}
